import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MdDirections, MdLocationOn } from 'react-icons/md';
import { SlArrowRight } from "react-icons/sl";
import { AiOutlineMinus, AiOutlinePlus, AiFillStar, AiOutlineStar, AiOutlineShopping, AiOutlineCheckCircle, AiOutlineHeart } from 'react-icons/ai';
import { useStateContext } from '../context/StateContext';
import { urlFor } from '../sanity/sanityClient';
import './ProductDesktop.css'; // Create this CSS file for additional styles

const ProductDesktop = ({ products }) => {
  const [index, setIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastProduct, setToastProduct] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const { setShowCart, decQty, incQty, qty, onAdd } = useStateContext();

  const handleAddToCart = (product) => {
    const focusedImage = product.image[index];
    const productWithFocusedImage = { ...product, image: [focusedImage] };
    onAdd(productWithFocusedImage, qty);

    setToastProduct({
      name: product.name,
      quantity: product.quantity || 1
    });
    setShowToast(true);
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastProduct(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleBuyNow = (product) => {
    const focusedImage = product.image[index];
    const productWithFocusedImage = { ...product, image: [focusedImage] };
    onAdd(productWithFocusedImage, qty);
    setShowCart(true);
  };

  const handleGetDirections = (productLocation) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLatitude = position.coords.latitude;
          const userLongitude = position.coords.longitude;
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLatitude},${userLongitude}&destination=${productLocation.lat},${productLocation.lng}&travelmode=driving`;
          window.open(directionsUrl, "_blank");
        },
        (error) => {
          console.error("Error fetching user location:", error);
          alert("Unable to fetch your location. Please allow location access.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  // If no products, return null
  if (!products || products.length === 0) return null;

  const product = products[0];

  return (
    <>
      {/* Toast Notification */}
      {showToast && (
        <div className="toast-notification">
          <AiOutlineCheckCircle size={24} color="#4caf50" />
          <div>
            <div className="toast-title">Added to Cart</div>
            <div className="toast-message">
              {toastProduct?.quantity} x {toastProduct?.name}
            </div>
          </div>
        </div>
      )}

      <div className="product-detail-container">

        <div className="product-detail-grid">
          {/* Left Column - Images */}
          <div className="product-images">
            <div className="main-image-container">
              {product.status && (
                <span className={`status-badge ${product.status}`}>
                  {product.status.toUpperCase()}
                </span>
              )}
              <button 
                className={`wishlist-button ${isLiked ? 'active' : ''}`}
                onClick={() => setIsLiked(!isLiked)}
              >
                <AiOutlineHeart />
              </button>
              <img
                src={urlFor(product.image && product.image[index])}
                alt={product.name}
                className="main-image"
              />
            </div>
            
            <div className="thumbnail-grid">
              {product.image.map((image, i) => (
                <button
                  key={i}
                  className={`thumbnail-button ${i === index ? 'active' : ''}`}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => setIndex(i)}
                >
                  <img
                    src={urlFor(image)}
                    alt={`${product.name} ${i + 1}`}
                    className="thumbnail-image"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right Column - Product Info */}
          <div className="product-info">
            <h1 className="product-title">{product.name}</h1>
            
            <div className="product-meta">
               {/* Location and Message Actions */}
            {(product.geolocation || product.user) && (
              <div className="service-actions">
                {product.geolocation && (
                  <button
                    className="directions-btn"
                    onClick={() => handleGetDirections(product.geolocation)}
                  >
                    <MdDirections />
                    <span>Get Directions</span>
                    <MdLocationOn className="location-icon" />
                  </button>
                )}
                
                
              </div>
            )}
              <div className="reviews-summary">
                <div className="stars">
                  <AiFillStar />
                  <AiFillStar />
                  <AiFillStar />
                  <AiFillStar />
                  <AiOutlineStar />
                </div>
                <span className="review-count">(4 reviews)</span>
              </div>
              
              {product.user && (
                  <Link 
                    to={{
                      pathname: `/chat/${product.user}`,
                      state: { productImage: product.image[0].asset._ref }
                    }}
                    className="message-btn"
                    onClick={() => {
                      localStorage.setItem('currentProduct', JSON.stringify({
                        image: product.image[0].asset._ref
                      }));
                    }}
                  >
                    Send Message
                    <SlArrowRight />
                  </Link>
                )}
            </div>

            <div className="product-price">
              <span className="currency">{product.currency || 'TZS'}</span>
              <span className="price">{product.price.toLocaleString()}/=</span>
            </div>

            {/* Tabs */}
            <div className="product-tabs">
              <button 
                className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
              <button 
                className={`tab-button ${activeTab === 'shipping' ? 'active' : ''}`}
                onClick={() => setActiveTab('shipping')}
              >
                Shipping
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'details' ? (
                <p className="product-description">{product.details}</p>
              ) : (
                <div className="shipping-info">
                  <p>Free shipping on orders over TZS 100,000</p>
                  <p>Estimated delivery: 3-5 business days</p>
                </div>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="quantity-selector">
              <span className="quantity-label">Quantity:</span>
              <div className="quantity-controls">
                <button onClick={decQty} className="quantity-btn">
                  <AiOutlineMinus />
                </button>
                <span className="quantity-value">{qty}</span>
                <button onClick={incQty} className="quantity-btn">
                  <AiOutlinePlus />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button 
                className="add-to-cart-btn"
                onClick={() => handleAddToCart(product)}
              >
                <AiOutlineShopping />
                Add to Cart
              </button>
              <button 
                className="buy-now-btn"
                onClick={() => handleBuyNow(product)}
              >
                Buy Now
              </button>
            </div>

           
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDesktop;